import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HotToastService } from '@ngneat/hot-toast';
import { AffiliateLinkType, AffiliateProductTypes } from 'src/app/core/dtos/affiliate-data';
import { AffiliateLinkDto, DeliveryMethodDto } from 'src/app/core/dtos/dto';
import { BrandEnum } from 'src/app/core/dtos/enum';
import { HttpErrorResponseMessages } from 'src/app/core/dtos/http-error-response-messages';
import { AccountService } from 'src/app/core/services/account.service';
import { ConfigHelper } from 'src/app/core/services/helpers/config.helper';
import { Title } from '@angular/platform-browser';

@Component({
  selector: 'app-link-create',
  templateUrl: './link-create.component.html',
  styleUrls: ['./link-create.component.scss']
})
export class LinkCreateComponent implements OnInit {
  brandName: string;
  brandId: BrandEnum;

  userForm: FormGroup;

  model: AffiliateLinkDto;

  isLinkCreate: boolean;
  isLinkEdit = false;
  isLinkShowing = false;

  deliveryMethods: DeliveryMethodDto[];
  productTypes: AffiliateProductTypes[] = [];

  affiliateLinkId: string;
  htmlContent: string;
  linkPlaceholderText: string;
  linkType: string;

  linkTypes: AffiliateLinkType[] = [
    { type: "Border", value: 1 },
    { type: "Skyscraper Banner", value: 2 },
    { type: "Leader Board", value: 4 },
    { type: "Text", value: 3 },
    { type: "Link", value: 5 }
  ];

  get f() { return this.userForm.controls; }

  constructor(
    private configHelper: ConfigHelper,
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private accountService: AccountService,
    private toastService: HotToastService,
    private router: Router,
    private titleService: Title,
  ) { }

  ngOnInit(): void {
    this.titleService.setTitle("Affiliate Links");
    this.brandId = this.configHelper.getBrandId();
    this.brandName = this.configHelper.getBrandName();

    this.route.queryParams.subscribe(params => {
      this.affiliateLinkId = params.linkId;
      this.isLinkCreate = params.linkcreate === "true" ? true : false;
      if (this.isLinkCreate === true) {
        this.isLinkEdit = true;
        return;
      }
    });

    this.userForm = this.formBuilder.group({
      productType: ["", Validators.required],
      deliveryMethod: ["", Validators.required],
      description: [null],
      linkType: [null, Validators.required],
      linkName: [null, Validators.required],
      toOpenNewWin: [null]
    });

    switch (this.brandId) {
      case BrandEnum.CAEL:
        this.productTypes = [{ type: "Registration", value: 0, isForIP: false, linkPlaceholderText: 'Take the CAEL test!' }];

        this.linkPlaceholderText = 'Take the CAEL test!';
        break;
      case BrandEnum.CELPIP:
        this.productTypes = [{ type: "Registration", value: 0, isForIP: false, linkPlaceholderText: 'Take the CELPIP test!' }, {
          type: "CELPIP Accelerate (Online Prep Program)", value: 72, isForIP: true, linkPlaceholderText: 'Get the CELPIP Accelerate!'
        }];
        this.linkPlaceholderText = 'Get the CELPIP Accelerate!';
        break;
      default:
        break;
    }

    this.populateAffiliateDetailMenu();
  }

  populateAffiliateDetailMenu() {
    this.accountService.getAffiliateDeliveryMethods().subscribe(
      result => {
        this.deliveryMethods = result.content;
        if (this.affiliateLinkId) {
          this.accountService
            .getAffiliateDetail(this.affiliateLinkId).subscribe(
              affiliateDetail => {
                this.model = affiliateDetail.content;
                const productType = !this.model.productId ? this.productTypes[0] : this.productTypes[1];
                this.model.productId = productType.value;
                this.htmlContent = this.generateAffiliateLinkUrl(this.model);
                const deliveryMethod = productType ? this.getDeliveryMethod(this.model) : null;
                this.userForm.setValue({
                  productType,
                  deliveryMethod,
                  description: this.model.description,
                  linkType: this.getLinkType(affiliateDetail.content.typeId),
                  linkName: this.model.linkName,
                  toOpenNewWin: this.model.toOpenNewWin,
                });
                this.isLinkShowing = true;
              },
              (error: HttpErrorResponse) => {
                if (error.status === 400) {
                  const errorMessage = error.error as HttpErrorResponseMessages;
                  errorMessage.reasons.forEach(reasons => {
                    this.toastService.warning(reasons.errorMessage);
                  });
                }
              });
        }
      },
      (error: HttpErrorResponse) => {
        if (error.status === 400) {
          const errorMessage = error.error as HttpErrorResponseMessages;
          errorMessage.reasons.forEach(reasons => {
            this.toastService.warning(reasons.errorMessage);
          });
        }
      });
  }

  close() {
    this.router.navigate(['my-account']);
  }

  copyHTML() {
    const text = document.getElementById("af-link-html-content");
    const selection = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(text);
    selection.removeAllRanges();
    selection.addRange(range);
    document.execCommand('copy');
  }

  copyLink() {
    let url: string;
    if (this.model.productId === 0 && this.model.deliveryMethodId === 2) {
      url = this.configHelper.getBaseUrl() + "/#/registration/sitting-selection-web-delivery?affiliate=" + this.model.guid + "&companyName=" + this.model.companyName;
    }
    else if (this.model.productId === 0) {
      url = this.configHelper.getBaseUrl() + "/#/registration/sitting-selection?affiliate=" + this.model.guid + "&companyName=" + this.model.companyName;
    } else {
      url = this.configHelper.getBaseUrl() + "/#/shopping-cart?affiliate=" + this.model.guid + "&companyName=" + this.model.companyName + "&productId=" + this.model.productId;
      return url;
    }
    // Copy the text inside the text field
    navigator.clipboard.writeText(url);  
  }

  getDeliveryMethod(link): DeliveryMethodDto {
    if (link.deliveryMethodId == null) {
      return this.deliveryMethods[0];
    }
    for (const methods of this.deliveryMethods) {
      if (link.deliveryMethodId === methods.id) {
        return methods;
      }
    }
  }

  getLinkType(typeId: number): AffiliateLinkType {
    for (const linkType of this.linkTypes) {
      if (typeId === linkType.value) {
        return linkType;
      }
    }
  }

  generateAffiliateLinkUrl(affiliateLink: AffiliateLinkDto) {
    let url: string;
    if (affiliateLink.productId === 0 && affiliateLink.deliveryMethodId === 2) {
      url = "<a style='width: 100%;' href='" + this.configHelper.getBaseUrl() + "/#/registration/sitting-selection-web-delivery?affiliate=" + affiliateLink.guid + "&companyName=" + affiliateLink.companyName + "' ";
    }
    else if (affiliateLink.productId === 0) {
      url = "<a style='width: 100%;' href='" + this.configHelper.getBaseUrl() + "/#/registration/sitting-selection?affiliate=" + affiliateLink.guid + "&companyName=" + affiliateLink.companyName + "' ";
    } else {
      url = this.configHelper.getBaseUrl() + "/#/shopping-cart?affiliate=" + affiliateLink.guid + "&companyName=" + affiliateLink.companyName + "&productId=" + affiliateLink.productId;
      return url;
    }

    if (affiliateLink.toOpenNewWin) {
      url += "target='_blank'";
    }
    url += " >";

    let imgFileBrandName = "";
    if (this.brandId === BrandEnum.CAEL) {
      imgFileBrandName = "CAEL";
    }
    else {
      imgFileBrandName = "CELPIP";
    }

    if (affiliateLink.typeId === 1) {
      url += "<img style='max-width: 100%;' src='" + this.configHelper.getBaseUrl() + "/Content/img/" + imgFileBrandName + "-border.png' alt='" + affiliateLink.linkName + "'>";
    } else if (affiliateLink.typeId === 2) {
      url += "<img style='max-width: 100%;' src='" + this.configHelper.getBaseUrl() + "/Content/img/" + imgFileBrandName + "-SkyscraperBanner.png' alt='" + affiliateLink.linkName + "'>";
    } else if (affiliateLink.typeId === 4) {
      url += "<img style='max-width: 100%;' src='" + this.configHelper.getBaseUrl() + "/Content/img/" + imgFileBrandName + "-Leaderboard.png' alt='" + affiliateLink.linkName + "'>";
    } else {
      url += affiliateLink.linkName;
    }

    return (url += "</a>");
  }

  previewLink() {
    //isForIP ones hides the linktype so needs to get filled in here
    if (this.f.productType.value.isForIP) {
      this.userForm.controls.linkType.setValue(this.getLinkType(5));
    }
    if (this.f.linkType.value) {
      if (!this.model) {
        this.model = {
          typeId: this.f.linkType.value.value,
          typeName: this.f.linkType.value.type,
          productId: this.f.productType.value.value,
          deliveryMethodId: this.f.deliveryMethod.value.id,
          linkName: this.f.linkName.value,
          description: this.f.description.value,
          toOpenNewWin: this.f.toOpenNewWin.value,
        } as AffiliateLinkDto;
      } else {
        this.model.typeId = this.f.linkType.value.value;
        this.model.typeName = this.f.linkType.value.type;
        this.model.productId = this.f.productType.value.value;
        this.model.deliveryMethodId = this.f.deliveryMethod.value.id;
        this.model.linkName = this.f.linkName.value;
        this.model.description = this.f.description.value;
        this.model.toOpenNewWin = this.f.toOpenNewWin.value;
        this.model.brandId = this.brandId;
      }
      this.htmlContent = !this.f.productType ? null : this.generateAffiliateLinkUrl(this.model);
    }
  }


  save() {
    //causes required text to pop up
    this.userForm.controls.deliveryMethod.markAsDirty();
    this.userForm.controls.linkType.markAsDirty();
    this.userForm.controls.productType.markAsDirty();
    this.userForm.controls.linkName.markAsDirty();

    if (this.f.productType.value.isForIP) {
      this.userForm.controls.linkType.setValue(this.getLinkType(5)); //5 is for type of link
    }
    if (this.f.linkType.errors?.required || this.f.linkName.errors?.required || this.f.productType.errors?.required) {
      this.toastService.warning("Invalid User Input, Please check the product type, link name and type, and try again.");
      return;
    }
    if (this.brandId === BrandEnum.CAEL && this.userForm.controls.productType.value.value === 0 && !this.userForm.controls.deliveryMethod.value.id) {
      this.toastService.warning("Invalid User Input, Please set the delivery method and try again.");
      return;
    }
    if (this.isLinkCreate) {
      this.createAffiliateLink();
    } else {
      this.updateAffiliateLink();
    }
  }

  toggleEdit() {
    this.isLinkEdit = !this.isLinkEdit;
  }

  createAffiliateLink() {
    this.isLinkCreate = true;
    this.accountService
      .createAffiliateLink(this.model).subscribe(
        result => {
          const link = result.content;
          this.toastService.success("Successful, A new affiliate link was created.");
          this.htmlContent = this.generateAffiliateLinkUrl(link);
          this.isLinkCreate = false;
          this.isLinkEdit = false;
          this.isLinkShowing = true;
          this.router.navigate(['account/affiliate/link-detail'], { queryParams: { linkId: result.content.id } });
        },
        (error: HttpErrorResponse) => {
          if (error.status === 400) {
            const errorMessage = error.error as HttpErrorResponseMessages;
            errorMessage.reasons.forEach(reasons => {
              this.toastService.warning(reasons.errorMessage);
            });
          }
        });
  }

  updateAffiliateLink() {
    this.accountService
      .updateAffiliateLink(this.model).subscribe(
        () => {
          this.toastService.success("Successful, Updated affiliate link.");
          this.htmlContent = this.generateAffiliateLinkUrl(this.model);
        },
        (error: HttpErrorResponse) => {
          if (error.status === 400) {
            const errorMessage = error.error as HttpErrorResponseMessages;
            errorMessage.reasons.forEach(reasons => {
              this.toastService.warning(reasons.errorMessage);
            });
          }
        });
  }
}
